import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { format } from 'date-fns'
import logger from '../utils/logger'

/**
 * Compatibility types matching old Supabase shape.
 * Convex data is mapped to these types in useCalendar hook
 * so that DayCell, CombinedModal, etc. don't need changes.
 */
export interface CalendarEntry {
  id: string // mapped from Convex _id
  child_id: string
  family_id: string
  date: string
  location: 'home_a' | 'home_b'
  comment: string | null
  is_encrypted: boolean
  created_by: string
  created_at: string
  updated_at: string
}

export interface CalendarChild {
  id: string // mapped from Convex _id
  family_id: string
  name: string
  birth_date: string | null
  created_at: string
}

interface CalendarState {
  // Data — populated by useCalendar hook from Convex reactive queries
  entries: CalendarEntry[]
  children: CalendarChild[]

  // UI State
  selectedChildId: string | null
  selectedHome: 'home_a' | 'home_b' | null
  currentDate: string
  viewMode: 'month' | 'week'
  isLoading: boolean
  error: string | null

  // Data setters (called from useCalendar hook)
  setEntries: (entries: CalendarEntry[]) => void
  setChildren: (children: CalendarChild[]) => void
  setIsLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void

  // UI actions
  setSelectedChild: (childId: string) => void
  setSelectedHome: (home: 'home_a' | 'home_b' | null) => void
  setCurrentDate: (date: string) => void
  setViewMode: (mode: 'month' | 'week') => void
  clearCache: () => void
}

export const useCalendarStore = create<CalendarState>()(
  persist(
    (set) => ({
      entries: [],
      children: [],
      selectedChildId: null,
      selectedHome: null,
      currentDate: format(new Date(), 'yyyy-MM-dd'),
      viewMode: 'month',
      isLoading: false,
      error: null,

      setEntries: (entries) => set({ entries }),

      setChildren: (children) => {
        set((state) => {
          let newSelectedChildId = state.selectedChildId

          // Auto-select first child if none selected or temp
          if ((!state.selectedChildId || state.selectedChildId === 'temp-child-id') && children.length > 0) {
            newSelectedChildId = children[0].id
          } else if (state.selectedChildId && state.selectedChildId !== 'temp-child-id') {
            // Check if selected child still exists
            const exists = children.some(c => c.id === state.selectedChildId)
            if (!exists) {
              newSelectedChildId = children.length > 0 ? children[0].id : null
            }
          }

          return { children, selectedChildId: newSelectedChildId }
        })
      },

      setIsLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      setSelectedChild: (childId) => {
        logger.debug('Child selected:', childId)
        // Clear entries when changing child to avoid data mixing
        set({ selectedChildId: childId, entries: [] })
      },

      setSelectedHome: (home) => {
        logger.debug('Home selected:', home)
        set({ selectedHome: home })
      },

      setCurrentDate: (date) => {
        logger.debug('Date changed to:', date)
        set({ currentDate: date })
      },

      setViewMode: (mode) => {
        logger.debug('View mode changed to:', mode)
        set({ viewMode: mode })
      },

      clearCache: () => {
        logger.debug('Clearing calendar cache')
        set({
          entries: [],
          selectedChildId: null,
          selectedHome: null,
        })
      },
    }),
    {
      name: '2homes-calendar-storage',
      partialize: (state) => ({
        selectedChildId: state.selectedChildId,
        selectedHome: state.selectedHome,
        viewMode: state.viewMode,
      }),
    }
  )
)
